import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "overlay", "panel"]

  connect() {
    this.containerTarget.addEventListener("turbo:submit-end", this._onSubmitEnd)
    this.containerTarget.addEventListener("turbo:before-fetch-response", this._onBeforeFetchResponse)
  }

  open(event) {
    // Re-entrancy guard: ignore activation while already open (prevents
    // clobbering the saved return URL and stranding the address bar).
    if (!this.containerTarget.classList.contains("hidden")) return

    const url = event?.currentTarget?.dataset?.drawerUrlValue
    if (url) {
      this._pushedHistory = true
      history.pushState({ drawer: true }, "", url)
    } else {
      this._pushedHistory = false
    }

    this.containerTarget.classList.remove("hidden")

    requestAnimationFrame(() => {
      this.overlayTarget.classList.add("opacity-100")
      this.overlayTarget.classList.remove("opacity-0")
      this.panelTarget.classList.remove("translate-x-full")
      this.panelTarget.classList.add("translate-x-0")
    })

    document.addEventListener("keydown", this._onKeydown)
    window.addEventListener("popstate", this._onPopState)
    this.element.addEventListener("turbo:frame-load", this._onFrameLoad)
  }

  // Manual close (ESC / overlay / close button). If we pushed a history entry
  // on open, collapse it with back() so we don't strand a third entry — the
  // popstate handler then performs the DOM close.
  close() {
    if (this._pushedHistory) {
      this._pushedHistory = false
      history.back()
      return
    }
    this._closePanel()
  }

  _onPopState = () => {
    this._pushedHistory = false
    this._closePanel()
  }

  _closePanel() {
    this.panelTarget.classList.add("translate-x-full")
    this.panelTarget.classList.remove("translate-x-0")
    this.overlayTarget.classList.add("opacity-0")
    this.overlayTarget.classList.remove("opacity-100")

    setTimeout(() => {
      this.containerTarget.classList.add("hidden")
    }, 300)

    document.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("popstate", this._onPopState)
    this.element.removeEventListener("turbo:frame-load", this._onFrameLoad)
  }

  _onKeydown = (event) => {
    if (event.key === "Escape") this.close()
  }

  // Focus the panel only after the turbo-frame content has actually rendered
  // (the frame fetch is async; focusing on the next paint would find an empty frame).
  _onFrameLoad = (event) => {
    if (event.target.id === "drawer_frame") this._focusPanel()
  }

  _focusPanel() {
    const focusable = this.panelTarget.querySelector("a, button, input, textarea, [tabindex]")
    ;(focusable || this.panelTarget).focus?.()
  }

  _onBeforeFetchResponse = (event) => {
    const response = event.detail.fetchResponse.response
    const contentType = response.headers.get("Content-Type") || ""
    if (contentType.includes("turbo-stream")) return

    if (response.redirected) {
      event.preventDefault()
    }
  }

  _onSubmitEnd = (event) => {
    if (event.detail.success) {
      const contentType = event.detail.fetchResponse.response.headers.get("Content-Type") || ""
      if (contentType.includes("turbo-stream")) return

      this._showSavedToast()
      setTimeout(() => {
        this._closePanel()
        Turbo.visit(event.detail.fetchResponse.response.url)
      }, 800)
    }
  }

  _showSavedToast() {
    const toast = document.createElement("div")
    toast.className = "drawer-toast"
    toast.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-4 w-4"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg> Salvato!`
    this.panelTarget.appendChild(toast)
    requestAnimationFrame(() => toast.classList.add("show"))
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("popstate", this._onPopState)
    this.element.removeEventListener("turbo:frame-load", this._onFrameLoad)
    this.containerTarget.removeEventListener("turbo:submit-end", this._onSubmitEnd)
    this.containerTarget.removeEventListener("turbo:before-fetch-response", this._onBeforeFetchResponse)
  }
}
